'use client';

import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useState } from 'react';

import { useToast } from '@/components/ui/use-toast';
import type { UploadOptions, UploadState } from '@/types/upload';
import { getUploadState, subscribeToUploads } from '@/utils/uploadStateManager';

import { useGalleryUpload } from './useMediaGalleryMutations';

/**
 * Hook to subscribe to global upload state
 */
export function useUploadState() {
  const [state, setState] = useState<UploadState>(() => getUploadState());

  useEffect(() => {
    const unsubscribe = subscribeToUploads(setState);
    return unsubscribe;
  }, []);

  return state;
}

/**
 * Centralized gallery upload system hook
 * Handles all upload scenarios with consistent behavior
 */
export function useGalleryUploadSystem(options: UploadOptions = {}) {
  const {
    pollingStrategy = 'list',
    multiple: _multiple = true,
    enableScrollLock: _enableScrollLock = false,
    onUploadStart,
    onUploadSuccess,
    onUploadComplete,
    onUploadProgress,
  } = options;

  const { toast } = useToast();
  const translate = useTranslations();

  // Use the existing gallery upload hook
  const { uploadFile, isUploading, error, reset } = useGalleryUpload({
    onUploadSuccess,
    pollingStrategy,
  });

  // Batch upload function
  const uploadFiles = useCallback(async (files: File[]) => {
    if (files.length === 0)
      return;

    // Show initial toast
    toast({
      title: translate('upload.status.uploadStarted'),
      description: translate('upload.status.processing', { count: String(files.length) }),
      variant: 'primary',
    });

    let successCount = 0;
    let errorCount = 0;

    // Upload files sequentially to avoid overwhelming the server
    for (const file of files) {
      try {
        if (onUploadStart) {
          onUploadStart(file);
        }

        await uploadFile({
          file,
          onProgress: onUploadProgress,
        });

        successCount++;
      } catch (error) {
        errorCount++;
        console.error(`Failed to upload ${file.name}:`, error);
      }
    }

    // Show completion toast
    if (successCount > 0) {
      toast({
        title: translate('upload.status.uploadComplete'),
        description: errorCount > 0
          ? translate('upload.status.successfullyUploadedWithFailed', { successCount: successCount.toString(), errorCount: errorCount.toString() })
          : translate('upload.status.successfullyUploaded', { count: String(successCount) }),
        variant: successCount === files.length ? 'primary' : 'error',
      });
    }

    // Call completion callback
    if (onUploadComplete) {
      await onUploadComplete();
    }
  }, [uploadFile, toast, translate, onUploadStart, onUploadComplete, onUploadProgress]);

  return {
    uploadFile,
    uploadFiles,
    isUploading,
    error,
    reset,
  };
}
